import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Paper,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FilledInput,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import TenantService from "../../services/tenantService";
import PermissionGroupService from "../../services/tenantServices/PermissionGroupService";
import TenantLayout from "../../layouts/TenantLayout";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SecurityIcon from "@mui/icons-material/Security";

/**
 * 📧 InviteAdminDoctor
 * Tenant tarafından admin doktor davet etme sayfası
 * Sadece email ile davet gönderir, kayıt link üzerinden tamamlanır
 */
export default function InviteAdminDoctor() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('tenantDashboard');
  const tenantService = new TenantService();
  const permissionService = new PermissionGroupService();
  const token = localStorage.getItem("tokenTenant");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [roleTitleError, setRoleTitleError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Permission Groups state
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  // Fetch permission groups on mount
  useEffect(() => {
    fetchPermissionGroups();
  }, []);

  const fetchPermissionGroups = async () => {
    try {
      setGroupsLoading(true);
      const groups = await permissionService.getAllGroups();
      // Filter only admin type groups
      const adminGroups = groups.filter(group => group.type === 'admin');
      setPermissionGroups(adminGroups);
    } catch (error) {
      console.error("Error fetching permission groups:", error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError("");
    setErrorMessage("");
  };

  const handleRoleTitleChange = (e) => {
    const value = e.target.value;
    setRoleTitle(value);
    setRoleTitleError("");
    setErrorMessage("");
  };

  const handleRoleTitleBlur = (e) => {
    const value = e.target.value;
    if (value.trim()) {
      const capitalizedValue = capitalizeWords(value);
      setRoleTitle(capitalizedValue);
    }
  };

  const handleGroupChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedGroups(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setErrorMessage("");
    setSuccessMessage("");

    // Email validation
    if (!email.trim()) {
      setEmailError(t('emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t('emailInvalid'));
      return;
    }

    // Role title validation
    if (!roleTitle.trim()) {
      setRoleTitleError(t('roleTitleRequired'));
      return;
    }

    setLoading(true);

    try {
      // Capitalize role title before submitting
      const capitalizedRoleTitle = capitalizeWords(roleTitle);
      
      const response = await tenantService.inviteAdminDoctor(
        email, 
        token,
        capitalizedRoleTitle,
        selectedGroups.length > 0 ? selectedGroups : null
      );
      setSuccessMessage(
        t('invitationEmailSent', { email })
      );
      setOpenSnackbar(true);
      setEmail(""); // Clear form
      setRoleTitle(""); // Clear role title
      setSelectedGroups([]); // Clear selected groups
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        t('errorSendingInvitation');
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        backgroundColor: "#f5f7fb",
        pt: { xs: 8, md: 10 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          width: "100%",
          maxWidth: "720px",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
            boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
            border: `1px solid ${colors.primary[300]}55`,
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 4 },
              py: { xs: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              gap: 3.5,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                textAlign: "center",
                background: `linear-gradient(135deg, ${colors.blueAccent[700]}99, ${colors.greenAccent[500]}55)`,
                borderRadius: 3,
                px: { xs: 3, md: 4 },
                py: { xs: 4, md: 5 },
                boxShadow: `0 18px 36px rgba(15, 23, 42, 0.25)`,
              }}
            >
              <AdminPanelSettingsIcon
                sx={{
                  fontSize: 60,
                  color: colors.grey[100],
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  color: colors.grey[100],
                  textTransform: "uppercase",
                }}
              >
                {t('inviteAdminDoctor')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: `${colors.grey[100]}cc`, mt: 1.5 }}
              >
                {t('inviteAdminDoctorDescription')}
              </Typography>
            </Box>

            {/* Info Box */}
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
                border: `1px solid ${colors.blueAccent[500]}55`,
                boxShadow: `0 14px 28px rgba(15, 23, 42, 0.3)`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: colors.grey[100],
                  mb: 1.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {t('howItWorks')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: `${colors.grey[100]}dd`, lineHeight: 1.9 }}
              >
                {t('howItWorksStep1')}
                <br />
                {t('howItWorksStep2')}
                <br />
                {t('howItWorksStep3')}
                <br />
                {t('howItWorksStep4')}
                <br />
                {t('howItWorksStep5')}
              </Typography>
            </Box>

            {/* Error Alert */}
            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  backgroundColor: `${colors.redAccent[500]}dd`,
                  color: colors.grey[100],
                  '& .MuiAlert-icon': { color: colors.grey[100] },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            {/* Success Message */}
            {successMessage && !openSnackbar && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  backgroundColor: `${colors.greenAccent[500]}dd`,
                  color: colors.grey[900],
                  '& .MuiAlert-icon': { color: colors.grey[900] },
                }}
              >
                {successMessage}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  backgroundColor: `${colors.primary[500]}cc`,
                  borderRadius: 3,
                  padding: { xs: 3, md: 4 },
                  boxShadow: `0 12px 26px rgba(15, 23, 42, 0.35)`,
                }}
              >
                {/* Email Field */}
                <TextField
                  fullWidth
                  label={t('doctorEmailAddress')}
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  error={Boolean(emailError)}
                  helperText={
                    emailError || t('invitationLinkSent')
                  }
                  variant="filled"
                  placeholder="doctor@example.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: colors.greenAccent[400] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: `${colors.grey[100]}aa`,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.greenAccent[400],
                    },
                    '& .MuiFilledInput-root': {
                      backgroundColor: `${colors.primary[600]}cc`,
                      borderRadius: 2,
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: `${colors.primary[600]}ee`,
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${colors.primary[600]}ff`,
                        boxShadow: `0 0 0 3px ${colors.greenAccent[400]}33`,
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      color: `${colors.grey[100]}aa`,
                    },
                  }}
                />

                {/* Role Title Field */}
                <TextField
                  fullWidth
                  label={t('roleTitle')}
                  name="role_title"
                  value={roleTitle}
                  onChange={handleRoleTitleChange}
                  onBlur={handleRoleTitleBlur}
                  error={Boolean(roleTitleError)}
                  helperText={
                    roleTitleError || t('roleTitleHelper')
                  }
                  variant="filled"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AdminPanelSettingsIcon sx={{ color: colors.blueAccent[400] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: `${colors.grey[100]}aa`,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.blueAccent[400],
                    },
                    '& .MuiFilledInput-root': {
                      backgroundColor: `${colors.primary[600]}cc`,
                      borderRadius: 2,
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: `${colors.primary[600]}ee`,
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${colors.primary[600]}ff`,
                        boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      color: `${colors.grey[100]}aa`,
                    },
                  }}
                />

                {/* Permission Groups Selection */}
                <FormControl 
                  fullWidth
                  variant="filled"
                  disabled={groupsLoading}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: `${colors.grey[100]}aa`,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.blueAccent[400],
                    },
                  }}
                >
                  <InputLabel id="permission-groups-label">
                    {t('permissionGroupsOptional')}
                  </InputLabel>
                  <Select
                    labelId="permission-groups-label"
                    id="permission-groups"
                    multiple
                    value={selectedGroups}
                    onChange={handleGroupChange}
                    input={<FilledInput />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const group = permissionGroups.find(g => g.id === value);
                          return (
                            <Chip 
                              key={value} 
                              label={group?.label || value}
                              size="small"
                              sx={{
                                backgroundColor: colors.blueAccent[700],
                                color: colors.grey[100],
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    startAdornment={
                      <InputAdornment position="start">
                        <SecurityIcon sx={{ color: colors.blueAccent[400], ml: 1 }} />
                      </InputAdornment>
                    }
                    sx={{
                      backgroundColor: `${colors.primary[600]}cc`,
                      borderRadius: 2,
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: `${colors.primary[600]}ee`,
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${colors.primary[600]}ff`,
                        boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                      },
                      '& .MuiSelect-icon': {
                        color: colors.grey[100],
                      },
                    }}
                  >
                    {groupsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {t('loadingGroups')}
                      </MenuItem>
                    ) : permissionGroups.length === 0 ? (
                      <MenuItem disabled>
                        {t('noPermissionGroups')}
                      </MenuItem>
                    ) : (
                      permissionGroups.map((group) => (
                        <MenuItem 
                          key={group.id} 
                          value={group.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: `${colors.blueAccent[700]}40`,
                            },
                            '&.Mui-selected': {
                              backgroundColor: `${colors.blueAccent[700]}60`,
                              '&:hover': {
                                backgroundColor: `${colors.blueAccent[700]}80`,
                              },
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <SecurityIcon sx={{ fontSize: 20, color: colors.blueAccent[400] }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {group.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                {group.name}
                              </Typography>
                            </Box>
                            {group.is_default && (
                              <Chip 
                                label={t('default')} 
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  backgroundColor: `${colors.blueAccent[800]}40`,
                                  color: colors.blueAccent[300],
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: `${colors.grey[100]}aa`,
                      mt: 0.5,
                      ml: 1.5,
                      display: 'block'
                    }}
                  >
                    {selectedGroups.length > 0 
                      ? t('groupsSelected', { count: selectedGroups.length })
                      : t('selectPermissionGroups')
                    }
                  </Typography>
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: "16px",
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                    boxShadow: `0 18px 32px rgba(46, 213, 145, 0.35)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                      boxShadow: `0 22px 40px rgba(46, 213, 145, 0.45)`,
                    },
                    '&:disabled': {
                      background: `${colors.grey[700]}aa`,
                      boxShadow: "none",
                    },
                  }}
                  endIcon={
                    loading ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <SendIcon />
                    )
                  }
                >
                  {loading ? t('sending') : t('sendInvitation')}
                </Button>
              </Box>
            </form>

            {/* Additional Info */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: `${colors.primary[600]}cc`,
                border: `1px solid ${colors.primary[400]}55`,
                boxShadow: `0 10px 24px rgba(15, 23, 42, 0.3)`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: `${colors.grey[100]}cc`,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                {t('securityNote')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: `${colors.grey[100]}dd`, lineHeight: 1.7 }}
              >
                {t('invitationValidDays')}
                <br />{t('doctorSetPassword')}
                <br />{t('emailAlreadyRegistered')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            backgroundColor: colors.greenAccent[500],
            boxShadow: `0 4px 16px rgba(76, 206, 172, 0.4)`,
            "& .MuiAlert-icon": {
              fontSize: "28px",
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "white" }}>
            {t('invitationSent')}
          </Typography>
          <Typography variant="body2" sx={{ color: "white", mt: 0.5 }}>
            {t('registrationLinkSent', { email })}
          </Typography>
        </Alert>
      </Snackbar>
    </TenantLayout>
  );
}


